import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Document } from '@contentful/rich-text-types';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import Footer from '../../components/Footer/Footer';
import { initializeDragScroll } from '../../utils/interactiveFeatures';
import { useBlogPost, useBlogPosts } from '../../hooks/useContentful';
import { ContentfulText, ContentfulRichText } from '../../components/Contentful';
import { images } from '../../components/shared/assets';
import { useTranslation } from '../../i18n';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../components/shared/CTALink/CTALink';
import './BlogDetailPage.css';

interface InstagramPost {
  src: string;
  alt: string;
  isVideo?: boolean;
}

const BlogDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  
  // Fetch blog post from Contentful
  const { data: blogPostData, loading } = useBlogPost(slug || '');
  const blogPost = blogPostData?.[0];
  
  // Fetch related posts
  const { data: allPostsData } = useBlogPosts(20);
  const relatedPosts = allPostsData?.filter(p => p.sys.id !== blogPost?.sys.id).slice(0, 4) || [];


  // Allow full viewport breakout for page break images
  useEffect(() => {
    // Add class to body to allow horizontal overflow for full viewport breakout
    document.body.classList.add('allow-full-viewport-breakout');
    
    // Cleanup: remove class when component unmounts
    return () => {
      document.body.classList.remove('allow-full-viewport-breakout');
    };
  }, []);

  // Initialize carousel interactions
  useEffect(() => {
    // Initialize drag scroll for the carousel
    const cleanup = initializeDragScroll('blog-related-carousel');
    
    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  // Scroll to top when blog post changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="blog-detail-page">
        <SmartNavigation forceVariant="light" />
        <div className="not-found">
          <h1>{t('common:loading.loading')}</h1>
        </div>
      </div>
    );
  }

  if (!blogPost) {
    return (
      <div className="blog-detail-page">
        <SmartNavigation forceVariant="light" />
        <div className="not-found">
          <h1>Blog Post Not Found</h1>
          <Link to="/blog">Back to Blog</Link>
        </div>
      </div>
    );
  }
  
  const fields = blogPost.fields;
  const featuredImage = fields.featuredImage as any;
  const featuredImageUrl = featuredImage?.fields?.file?.url;

  return (
    <div className="blog-detail-page">
      {/* Navigation Header - matching art immersions page */}
      <SmartNavigation forceVariant="light" />
      
      {/* Orange vertical line accent - matching art immersions page */}
      <div className="vertical-accent-line" />
      
      {/* 1. Header Section - Figma Exact Layout */}
      <section className="blog-detail-header">
        <div className="header-text-block">
          <div className="header-content">
            {/* Main Title - Using design system typography */}
            <h1 className="blog-detail-title">
              <ContentfulText content={fields.title} fallback="Untitled" />
            </h1>
            
            {/* Author + Date Block - Using design system typography */}
            <div className="blog-detail-meta">
              <div className="meta-location-date">
                <span className="meta-location">
                  <ContentfulText content={fields.author || 'AWASI'} fallback="AWASI" />
                </span>
                <span className="meta-separator">-</span>
                <span className="meta-date">
                  <ContentfulText content={fields.date || '2024'} fallback="2024" />
                </span>
              </div>
              <div className="meta-author">BY GABRIEL ORORKE</div>
            </div>
          </div>
        </div>
        
        {/* Social Icons - Positioned at bottom of section */}
        <div className="social-icons">
            <div className="social-icon twitter-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" fill="currentColor"/>
              </svg>
            </div>
            <div className="social-icon facebook-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="currentColor"/>
            </svg>
            </div>
            <div className="social-icon link-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
        </div>
      </section>

      {/* 2. Hero Image Section - Full width */}
      <section className="blog-detail-hero-image">
        <img 
          src={featuredImageUrl ? `https:${featuredImageUrl}` : images.galleryWaterfallLandscape} 
          alt={String(fields.title || 'Blog post')} 
        />
      </section>

      {/* 3. Article Content - Dynamic HTML content */}
      <section className="blog-block">
        <div className="blog-content">
          <div className="article-content">
            {fields.content && typeof fields.content === 'object' && 'nodeType' in fields.content ? (
              <ContentfulRichText content={fields.content as Document} />
            ) : (
              <ContentfulText content={fields.content} fallback="" />
            )}
          </div>
        </div>
        
        {/* Call to Action Buttons */}
        <div className="cta-buttons">
          <HandDrawnButton
            text="GO TO THE TOP"
            variant="brand"
            color="awasi"
            animation="stroke-to-fill"
            onClick={() => window.scrollTo(0, 0)}
            className="blog-cta-primary"
          />
          <CTALink
            text="BACK TO BLOG"
            type="secondary"
            color="awasi"
            href="/blog"
            className="blog-cta-secondary"
          />
        </div>
      </section>

      {/* 4. You Might Enjoy Carousel Section - Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="you-might-enjoy-section">
          <div className="you-might-enjoy-content">
            {/* Text Content Section - Title above like other in-page carousels */}
            <div className="you-might-enjoy-text-content">
              <h2 className="you-might-enjoy-title">You might enjoy</h2>
            </div>

            {/* Carousel Section - Cards below */}
            <div className="you-might-enjoy-carousel" id="blog-related-carousel">
              {relatedPosts.map((post) => {
                const postFields = post.fields;
                const postImage = postFields.featuredImage as any;
                const postImageUrl = postImage?.fields?.file?.url;
                const postSlug = postFields.slug || post.sys.id;
                
                return (
                  <div key={post.sys.id} className="carousel-card">
                    <div className="card-image-container">
                      <img 
                        src={postImageUrl ? `https:${postImageUrl}` : images.galleryWaterfallLandscape} 
                        alt={String(postFields.title || 'Blog post')} 
                      />
                    </div>
                    <div className="card-content">
                      <div className="card-header">
                        <h3 className="card-title">
                          <ContentfulText content={postFields.title} fallback="Untitled" />
                        </h3>
                        <div className="card-meta">
                          <span className="meta-location">
                            <ContentfulText content={postFields.author || 'AWASI'} fallback="AWASI" />
                          </span>
                          <span className="meta-separator">-</span>
                          <span className="meta-date">
                            <ContentfulText content={postFields.date || '2024'} fallback="2024" />
                          </span>
                        </div>
                      </div>
                      <p className="card-description">
                        <ContentfulText content={postFields.excerpt} fallback="" />
                      </p>
                      <div className="card-links">
                        <CTALink
                          text="READ MORE"
                          type="primary"
                          color="awasi"
                          href={`/blog/${postSlug}`}
                          className="read-more-link"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 5. Instagram Section - matching other pages */}
      <InstagramSection />

      {/* 6. Footer - matching other pages */}
      <Footer variant="default" />
    </div>
  );
};

export default BlogDetailPage;